"use client";

import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Table from "../components/common/Table";
import Modal from "../components/common/Modal";
import PaymentForm from "../components/payments/PaymentForm";
import api from "../services/api";
import Swal from "sweetalert2";

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await api.get("/payments");
      setPayments(response.data.data || []);
    } catch (error) {
      console.error("Error fetching payments:", error);
      toast.error("Failed to load payments");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async (paymentData) => {
    try {
      await api.post("/payments", paymentData);
      toast.success("Payment recorded successfully");
      setShowAddModal(false);
      fetchPayments();
    } catch (error) {
      console.error("Error recording payment:", error);
      toast.error(error.response?.data?.message || "Failed to record payment");
    }
  };
  const handleDownloadInvoice = async (paymentId) => {
    try {
      const response = await api.get(`/payments/${paymentId}/invoice`, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();

      // Clean up the link and blob URL after download
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Invoice download error:", error);
      toast.error("Failed to download invoice");
    }
  };

  const handleRefund = async (paymentId) => {
    // Show SweetAlert2 confirmation dialog
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning", // Warning icon
      showCancelButton: true, // Show cancel button
      confirmButtonColor: "#3085d6", // Confirm button color
      cancelButtonColor: "#d33", // Cancel button color
      confirmButtonText: "Yes, refund it!", // Confirm button text
    });

    // If user confirms, proceed with refund
    if (result.isConfirmed) {
      try {
        await api.post(`/payments/${paymentId}/refund`);
        toast.success("Payment refunded successfully");
        fetchPayments(); // Refresh the payments list
      } catch (error) {
        console.error("Error refunding payment:", error);
        toast.error(
          error.response?.data?.message || "Failed to refund payment"
        );
      }
    }
  };

  const filteredPayments = payments
    .filter(
      (payment) => selectedStatus === "all" || payment.status === selectedStatus
    )
    .filter((payment) => {
      const studentName = `${payment.student?.firstName || ""} ${
        payment.student?.lastName || ""
      }`.toLowerCase();
      const courseName = payment.course?.name?.toLowerCase() || "";
      const search = searchTerm.toLowerCase();
      return studentName.includes(search) || courseName.includes(search);
    });

  const columns = [
    {
      field: "student",
      header: "Student",
      render: (payment) =>
        `${payment.student?.firstName || ""} ${
          payment.student?.lastName || ""
        }` || "Unknown Student",
      className: "text-left px-4 py-2 font-semibold",
    },
    {
      field: "course",
      header: "Course",
      render: (payment) => payment.course?.name || "Unknown Course",
      className: "text-left px-4 py-2",
    },
    {
      field: "amount",
      header: "Amount",
      render: (payment) => `$${payment.amount.toFixed(2)}`,
      className: "text-right px-4 py-2",
    },
    {
      field: "paymentDate",
      header: "Payment Date",
      render: (payment) => new Date(payment.paymentDate).toLocaleDateString(),
      className: "text-center px-4 py-2",
    },
    {
      field: "paymentMethod",
      header: "Method",
      render: (payment) => payment.paymentMethod || "N/A",
      className: "text-center px-4 py-2",
    },
    {
      field: "status",
      header: "Status",
      render: (payment) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            payment.status === "completed"
              ? "bg-green-100 text-green-800"
              : payment.status === "pending"
              ? "bg-yellow-100 text-yellow-800"
              : payment.status === "refunded"
              ? "bg-red-100 text-red-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {payment.status}
        </span>
      ),
      className: "text-center px-4 py-2",
    },
    {
      field: "actions",
      header: "Actions",
      render: (payment) => (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => handleDownloadInvoice(payment._id)}
            className="px-3 py-2 text-white transition duration-300 bg-blue-500 rounded-md shadow-sm hover:bg-blue-600"
          >
            Invoice
          </button>
          {payment.status === "completed" && (
            <button
              className="px-3 py-2 text-white transition duration-300 bg-red-500 rounded-md shadow-sm hover:bg-red-600"
              onClick={() => handleRefund(payment._id)}
            >
              Refund
            </button>
          )}
        </div>
      ),
      className: "text-center px-4 py-2",
    },
  ];

  return (
    <div className="p-6 space-y-8 payments-container">
      <div className="flex items-center justify-between mb-6 page-header">
        <h1 className="text-3xl font-semibold text-gray-800">Payments</h1>
        <button
          className="px-6 py-3 text-white transition duration-300 bg-indigo-600 rounded-md hover:bg-indigo-700"
          onClick={() => setShowAddModal(true)}
        >
          Record Payment
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center">
          <div className="w-12 h-12 mx-auto border-b-4 border-indigo-600 rounded-full animate-spin"></div>
          <p className="mt-4 text-lg text-gray-600">Loading payments...</p>
        </div>
      ) : (
        <>
          <div className="p-6 mb-6 rounded-lg shadow-md summary-section bg-gray-50">
            <h2 className="mb-4 text-xl font-semibold text-gray-700">
              Payment Summary
            </h2>
            <p className="text-lg">
              Total Payments:{" "}
              <span className="font-medium">{filteredPayments.length}</span>
            </p>
            <p className="text-lg">
              Total Amount: $
              <span className="font-medium">
                {filteredPayments
                  .reduce((sum, payment) => sum + payment.amount, 0)
                  .toFixed(2)}
              </span>
            </p>
          </div>

          <div className="flex items-center gap-4 mb-6 filter-section">
            <label
              htmlFor="status-filter"
              className="font-medium text-gray-700"
            >
              Filter by Status:
            </label>
            <select
              id="status-filter"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg"
            >
              <option value="all">All</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="refunded">Refunded</option>
            </select>
            <input
              type="text"
              placeholder="Search by name or course..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          {filteredPayments.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              No payments found
            </div>
          ) : (
            <div className="overflow-x-auto bg-white rounded-lg shadow-md">
              <table className="min-w-full table-auto">
                <thead className="text-gray-700 bg-gray-100 border-b">
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col.field}
                        className={`text-left px-6 py-3 text-sm font-medium ${col.className}`}
                      >
                        {col.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment, index) => (
                    <tr
                      key={payment._id}
                      className={`${
                        index % 2 === 0 ? "bg-gray-50" : "bg-white"
                      } border-b`}
                    >
                      {columns.map((col) => (
                        <td
                          key={col.field}
                          className={`text-sm ${col.className}`}
                        >
                          {col.render(payment)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showAddModal && (
        <Modal
          title="Record Payment"
          onClose={() => setShowAddModal(false)}
          size="large"
        >
          <PaymentForm
            onSave={handleAddPayment}
            onCancel={() => setShowAddModal(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default Payments;
